import { useLayoutEffect, useRef, type ReactNode } from 'react';
import type { MentionActorSource } from '../../state/mentionUtils';
import { Tooltip } from '../../../common/Tooltip';
import { MaterialMentionPopup } from '../MaterialMentionPopup';
import {
  CUSTOM_SPEAKER_VALUE,
  DIALOGUE_LINE_LABEL_MAX_CHARS,
  DIALOGUE_LINE_TEXT_MAX_CHARS,
  buildDialogueSpeakerCandidates,
  createEmptyDialogueLine,
  ensureSpeakerOptionIncludesCurrent,
  resolveDialogueSpeakerChip,
  resolveDialogueSpeakerSelection,
  speakerToSelectValue,
  splitLineTextInHalf,
  type DialogueSpeakerOption,
  type ExtraSpeakerOption,
  type SpeakerLine,
} from '../dialogueLines';
import { DialogueSpeakerChip } from './DialogueSpeakerChip';
import { useDialogueSpeakerDropdown } from './useDialogueSpeakerDropdown';
import styles from './DialogueLines.module.css';

// actors 省略時の既定値 (DialogueLinesView と同じ理由)。
const EMPTY_ACTORS: MentionActorSource[] = [];

/** 行数・文字数の上限。省略時はセリフ (dialogue_lines) の値 (行数は上限なし)。 */
export interface SpeakerLinesEditorLimits {
  maxCount?: number;
  textMaxChars?: number;
  labelMaxChars?: number;
}

/** 文言。省略時はセリフ用。 */
export interface SpeakerLinesEditorLabels {
  textPlaceholder?: string;
  emptyHint?: string;
}

interface DialogueLinesEditorProps<L extends SpeakerLine> {
  lines: L[];
  onChange: (lines: L[]) => void;
  /** シナリオのキャスト俳優由来の選択肢 (casting_model_ids + seedance_preset_actor_ids)。 */
  speakerOptions: DialogueSpeakerOption[];
  /**
   * キャスト俳優以外の追加候補 (登場人物宣言 / ナレーション / 話者なし)。テロップ行
   * (telop_lines) の編集で telopLines.ts が渡す。セリフでは省略 (従来どおり)。
   */
  extraOptions?: ExtraSpeakerOption[];
  /** 行数・文字数の上限 (声の台本は8行・セリフとテロップは行数上限なし)。 */
  limits?: SpeakerLinesEditorLimits;
  labels?: SpeakerLinesEditorLabels;
  /** 「+ 行を追加」で足す行の初期値。省略時は空のセリフ行。 */
  createEmptyLine?: () => L;
  /**
   * 映像・ビジュアル欄の @メンション候補と同じ俳優配列 (SceneDetailColumn の
   * mentionActorSources)。select 隣の話者チップ (顔写真+バッジ) と option 文言の
   * バッジ前置に使う。未指定時はチップ・バッジなし (名前のみ) で従来どおり表示する。
   */
  actors?: MentionActorSource[];
  /**
   * footer の「+ 行を追加」と同じ行の右端に差し込む保存/キャンセル (呼び出し側の
   * SceneDetailColumn が inlineEditSave/inlineEditCancel ボタンを渡す。
   * 2026-08-17 要件5: 構造化セリフエディタに限り、フォーム下の独立行ではなく
   * footer 1行に同居させる)。未指定なら footer には「+ 行を追加」のみ表示する。
   */
  actionsSlot?: ReactNode;
  /**
   * テキスト欄 (セリフ入力・自由入力の話者名) での Enter 押下で保存を要求する
   * (2026-08-17 ユーザー指示)。日本語 IME の変換確定 Enter (isComposing) と、
   * 話者ドロップダウンが開いている間 (Enter=候補選択) は発火させない。
   */
  onRequestSave?: () => void;
  /**
   * テキスト欄で Shift+Enter による改行を許可する (テロップ用。1 テロップ内の改行は
   * 動画エディタで同じタイミングに複数行で表示される)。省略時 (セリフ) は改行不可で、
   * 貼り付け等で混入した改行も除去する。Enter 単独は従来どおり保存。
   * 改行を含む行には削除ボタンの下に「テロップを分割」ボタンを出す。
   */
  allowLineBreaks?: boolean;
}

/**
 * 構造化モード (`dialogue_lines` が非 NULL) のセリフ行単位編集UI。
 * 行ごとに「話者ピッカー (+自由入力時はラベル入力) ＋ セリフ入力 ＋ 削除」、
 * 末尾に行追加ボタン。全行削除しても呼び出し側の state は `[]` のまま保つ
 * (NULL には戻さない。docs/features/scenario-dialogue-speaker-plan.md §3.8)。
 *
 * 話者ピッカーは映像・ビジュアル欄の @メンション候補ポップアップ (MaterialMentionPopup)
 * をそのまま流用する (サムネイル＋バッジ＋名前の候補行・見た目とも同一)。トリガーは
 * 「C: Singer」のようなテキストボタンではなく、話者チップ (顔写真+バッジ) そのもの
 * (自由入力時はラベルテキスト、未選択時はプレースホルダ) — クリック/キー操作で開閉する
 * 点だけが @入力との違い。閉状態では俳優名を一切表示しない (2026-08-17 ユーザー指示)。
 *
 * 「+ 行を追加」で行を足すと、その新規行の話者ドロップダウンを自動で開く (会話の
 * 掛け合いを想定し、初期ハイライトは直前行の話者以外の先頭候補にする)。追加直後は
 * トリガー要素が未マウントのため、DOM 確定後の useLayoutEffect で開く。
 */
const EMPTY_EXTRA_OPTIONS: ExtraSpeakerOption[] = [];

export function DialogueLinesEditor<L extends SpeakerLine>({
  lines,
  onChange,
  speakerOptions,
  extraOptions = EMPTY_EXTRA_OPTIONS,
  limits,
  labels,
  createEmptyLine,
  actors = EMPTY_ACTORS,
  actionsSlot,
  onRequestSave,
  allowLineBreaks = false,
}: DialogueLinesEditorProps<L>) {
  const speakerDropdown = useDialogueSpeakerDropdown();
  const maxCount = limits?.maxCount ?? Infinity;
  const textMaxChars = limits?.textMaxChars ?? DIALOGUE_LINE_TEXT_MAX_CHARS;
  const labelMaxChars = limits?.labelMaxChars ?? DIALOGUE_LINE_LABEL_MAX_CHARS;
  const textPlaceholder = labels?.textPlaceholder ?? 'セリフ';
  const emptyHint = labels?.emptyHint ?? '（セリフなし）';

  // テキスト欄の Enter で保存 (2026-08-17 ユーザー指示)。日本語 IME の変換確定
  // Enter (isComposing) では発火しない。ドロップダウンが開いている間の Enter は
  // 候補選択の役割なので保存しない。
  const handleTextInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key !== 'Enter') return;
    if (e.nativeEvent.isComposing) return;
    if (speakerDropdown.dropdown.openId !== null) return;
    // Shift+Enter は改行 (allowLineBreaks のときだけ textarea の既定動作に任せる。
    // 他のテキスト欄 (映像/ナレーション等の textarea) と同じ流儀)。
    if (e.shiftKey && allowLineBreaks && e.currentTarget.tagName === 'TEXTAREA') return;
    // Enter 単独は改行させず、保存要求だけ出す (セリフは BE の strict 検証で改行が 422)。
    e.preventDefault();
    e.stopPropagation();
    onRequestSave?.();
  };
  // 各行のトリガー要素 (話者チップ/ラベル/プレースホルダを内包する role=button div)。
  // 行追加直後の自動オープンで、新規行のトリガーへ位置決めするために参照する。
  const triggerRefs = useRef<Array<HTMLElement | null>>([]);
  // addLine で足した行の index。null でなければ次の commit 後に自動オープンする
  // (DOM 未マウントの直後には開けないため useLayoutEffect 側で処理する)。
  const pendingAutoOpenIndexRef = useRef<number | null>(null);

  const updateLine = (idx: number, patch: Partial<L>) => {
    onChange(lines.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
  };
  const updateSpeaker = (idx: number, patch: Partial<SpeakerLine['speaker']>) => {
    onChange(lines.map((line, i) => (i === idx ? { ...line, speaker: { ...line.speaker, ...patch } } : line)));
  };
  const removeLine = (idx: number) => {
    onChange(lines.filter((_, i) => i !== idx));
  };
  // 隣の行と入れ替える (上下シェブロン)。開いている話者ドロップダウンは行 index で
  // 紐づくため閉じてから並べ替える。
  const moveLine = (idx: number, delta: -1 | 1) => {
    const target = idx + delta;
    if (target < 0 || target >= lines.length) return;
    speakerDropdown.dropdown.close();
    const next = [...lines];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  // 改行を含む行 (テロップ) を 2 つの行に分ける。話者などの他の項目は両方に引き継ぐ。
  const splitLine = (idx: number) => {
    const halves = splitLineTextInHalf(lines[idx].text);
    if (!halves || lines.length >= maxCount) return;
    speakerDropdown.dropdown.close();
    const [first, second] = halves;
    onChange([
      ...lines.slice(0, idx),
      { ...lines[idx], text: first },
      { ...lines[idx], text: second },
      ...lines.slice(idx + 1),
    ]);
  };
  const addLine = () => {
    if (lines.length >= maxCount) return;
    const newIndex = lines.length;
    const empty = (createEmptyLine ? createEmptyLine() : createEmptyDialogueLine()) as L;
    onChange([...lines, empty]);
    pendingAutoOpenIndexRef.current = newIndex;
  };

  // 行追加直後の自動オープン。新規行のトリガーが commit されてから (ref が埋まってから)
  // 開く必要があるため useLayoutEffect (ちらつき防止のため paint 前に反映)。
  useLayoutEffect(() => {
    const idx = pendingAutoOpenIndexRef.current;
    if (idx === null) return;
    pendingAutoOpenIndexRef.current = null; // 成否に関わらず一度きりの試行で消費する
    const triggerEl = triggerRefs.current[idx];
    const line = lines[idx];
    if (!triggerEl || !line) return;

    const options = ensureSpeakerOptionIncludesCurrent(speakerOptions, line.speaker);
    const candidates = buildDialogueSpeakerCandidates(options, actors, extraOptions);
    // 初期ハイライトは「直前行の話者」と異なる最初の候補 (会話の掛け合いを想定し、
    // 直前に話した俳優をデフォルト選択肢にしない)。先頭行、または全候補が直前話者と
    // 同一 (自由入力のみ等) の場合は先頭候補にフォールバックする。
    let initialActiveIndex = 0;
    if (idx > 0) {
      const prevValue = speakerToSelectValue(lines[idx - 1].speaker);
      const diffIdx = candidates.findIndex((c) => c.target.id !== prevValue);
      initialActiveIndex = diffIdx === -1 ? 0 : diffIdx;
    }
    speakerDropdown.openWithActiveIndex(String(idx), triggerEl, initialActiveIndex);
    triggerEl.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lines 変化のたびに pending フラグを見るだけで良い
  }, [lines]);

  // 現在開いている行の候補・選択肢 (ポップアップは1インスタンスのみ描画するため、
  // 開いている行のものだけをここで解決する)。
  const openRowIdx = speakerDropdown.dropdown.openId !== null ? Number(speakerDropdown.dropdown.openId) : null;
  const openLine = openRowIdx !== null ? lines[openRowIdx] : undefined;
  const openOptions = openLine ? ensureSpeakerOptionIncludesCurrent(speakerOptions, openLine.speaker) : [];
  const openCandidates = openLine ? buildDialogueSpeakerCandidates(openOptions, actors, extraOptions) : [];

  return (
    <div className={styles.editor} data-scene-inline-edit>
      {lines.length === 0 && <span className={styles.emptyHint}>{emptyHint}</span>}
      {lines.map((line, idx) => {
        const rowId = String(idx);
        const options = ensureSpeakerOptionIncludesCurrent(speakerOptions, line.speaker);
        const candidates = buildDialogueSpeakerCandidates(options, actors, extraOptions);
        const selectValue = speakerToSelectValue(line.speaker);
        const isOpen = speakerDropdown.isOpenFor(rowId);
        const speakerChip = resolveDialogueSpeakerChip(line.speaker, actors);
        const special = line.speaker.special;
        return (
          <div className={styles.lineRow} key={idx}>
            {/* 話者チップ (顔写真+バッジ) 自体がトリガー。「C: Singer」のようなテキスト
                トリガーは廃止し、閉状態で俳優名は一切表示しない。チップが無い場合は
                自由入力ラベル (あれば) かプレースホルダをトリガー文言にする
                (2026-08-17 ユーザー指示)。枠線・塗り (ピル形状) は持たず、顔写真の
                拡大 (size=lg) ＋ 右のシェブロンだけでクリック可能性を示す (要件1)。 */}
            <div
              ref={(el) => { triggerRefs.current[idx] = el; }}
              role="button"
              tabIndex={0}
              className={styles.speakerTrigger}
              data-testid={`dialogue-speaker-trigger-${idx}`}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-label="話者"
              title="話者"
              onClick={(e) => speakerDropdown.toggle(rowId, e.currentTarget, candidates, selectValue)}
              onKeyDown={(e) => speakerDropdown.handleTriggerKeyDown(
                e,
                rowId,
                e.currentTarget,
                candidates,
                selectValue,
                (candidate) => updateSpeaker(idx, resolveDialogueSpeakerSelection(candidate.target.id, line.speaker.label, options, extraOptions)),
              )}
            >
              {speakerChip ? (
                <DialogueSpeakerChip badge={speakerChip.badge} thumbnailUrl={speakerChip.thumbnailUrl} name={speakerChip.name} size="lg" />
              ) : special === 'narration' || line.speaker.kind === 'narration' ? (
                // テロップ話者「ナレーション」(telopLines.ts)・声の台本のナレーター行: マイクの円形チップ
                <DialogueSpeakerChip badge="" thumbnailUrl={null} name={line.speaker.label || 'ナレーション'} size="lg" glyph="mic" />
              ) : special === 'none' ? (
                <span className={`${styles.speakerTriggerLabel} ${styles.speakerTriggerPlaceholder}`}>話者なし</span>
              ) : line.speaker.label ? (
                // 自由入力の話者は同じ円サイズのダミー人物アイコン (バッジなし)。
                // 名前は隣の話者名入力欄が担う。2026-08-17 ユーザー指示。
                <DialogueSpeakerChip badge="" thumbnailUrl={null} name={line.speaker.label} size="lg" />
              ) : (
                <span className={`${styles.speakerTriggerLabel} ${styles.speakerTriggerPlaceholder}`}>話者を選択</span>
              )}
              {/* 下向きシェブロン (˅)。細いストローク・小さめ・控えめな色 (要件1)。 */}
              <svg viewBox="0 0 10 6" width="8" height="5" aria-hidden="true" className={styles.speakerTriggerChevron}>
                <path d="M1 1 L5 5 L9 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
            {selectValue === CUSTOM_SPEAKER_VALUE && (
              <input
                className={styles.speakerLabelInput}
                type="text"
                value={line.speaker.label}
                maxLength={labelMaxChars}
                placeholder="話者名"
                onChange={(e) => updateSpeaker(idx, { label: e.target.value })}
                onKeyDown={handleTextInputKeyDown}
              />
            )}
            <AutoGrowTextarea
              className={styles.lineTextInput}
              value={line.text}
              maxLength={textMaxChars}
              placeholder={textPlaceholder}
              // 改行不可のとき (セリフ) は貼り付け等で混入した改行を除去する (1 行 = 1 セリフ)
              onChange={(e) => updateLine(idx, {
                text: allowLineBreaks ? e.target.value.replace(/\r\n?/g, '\n') : e.target.value.replace(/[\r\n]+/g, ' '),
              } as Partial<L>)}
              onKeyDown={handleTextInputKeyDown}
            />
            {/* 並べ替え: 上下シェブロンを縦に重ねる (先頭行の上・末尾行の下は無効)。 */}
            {lines.length > 1 && (
              <div className={styles.moveLineButtons}>
                <button
                  type="button"
                  className={styles.moveLineButton}
                  onClick={() => moveLine(idx, -1)}
                  disabled={idx === 0}
                  aria-label="この行を上へ移動"
                  title="上へ移動"
                >
                  <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
                    <path d="M1 5 L5 1 L9 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={styles.moveLineButton}
                  onClick={() => moveLine(idx, 1)}
                  disabled={idx === lines.length - 1}
                  aria-label="この行を下へ移動"
                  title="下へ移動"
                >
                  <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
                    <path d="M1 1 L5 5 L9 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </button>
              </div>
            )}
            <div className={styles.lineSideActions}>
              {/* サイト共通の Tooltip コンポーネントで包む (ネイティブ title は外す。
                  aria-label は維持。2026-08-17 要件3)。 */}
              <Tooltip content="この行を削除">
                <button
                  type="button"
                  className={styles.removeLineButton}
                  onClick={() => removeLine(idx)}
                  aria-label="この行を削除"
                >
                  {/* 細いライン描画の X (remove_bold.svg は太いため使わない。2026-08-17 ユーザー指示) */}
                  <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                    <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
                  </svg>
                </button>
              </Tooltip>
              {/* 改行を含むテロップだけ: 行数の中央で 2 つのテロップに分ける */}
              {allowLineBreaks && line.text.includes('\n') && (
                <Tooltip content="テロップを2つに分割">
                  <button
                    type="button"
                    className={styles.removeLineButton}
                    onClick={() => splitLine(idx)}
                    disabled={lines.length >= maxCount}
                    aria-label="テロップを2つに分割"
                  >
                    <img src="/scissors_2.svg" alt="" width={12} height={12} />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        );
      })}
      {/* footer: 左に「+ 行を追加」、右に actionsSlot (保存/キャンセル・要件5)。 */}
      <div className={styles.editorFooter}>
        <button
          type="button"
          className={styles.addLineButton}
          onClick={addLine}
          disabled={lines.length >= maxCount}
        >
          + 行を追加
        </button>
        {actionsSlot}
      </div>
      <MaterialMentionPopup
        ref={speakerDropdown.dropdown.menuRef}
        candidates={openCandidates}
        activeIndex={speakerDropdown.activeIndex}
        position={speakerDropdown.dropdown.pos}
        onHoverIndex={speakerDropdown.setActiveIndex}
        onSelect={(candidate) => {
          if (openRowIdx === null || !openLine) return;
          updateSpeaker(openRowIdx, resolveDialogueSpeakerSelection(candidate.target.id, openLine.speaker.label, openOptions, extraOptions));
          speakerDropdown.dropdown.close();
        }}
      />
    </div>
  );
}

/**
 * 内容に応じて高さが伸びる 1 行起点の textarea。長いセリフ (最大 100 文字) を横スクロール
 * させず折り返して全文見えるようにする (2026-09-18 ユーザー指示。構造化前のセリフ欄は
 * 複数行 textarea だった)。改行の可否は親の onKeyDown / onChange (allowLineBreaks) が決める。
 */
function AutoGrowTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [props.value]);
  return <textarea ref={ref} rows={1} {...props} />;
}
